import React, { useCallback, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { programsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import BusinessCard from 'components/_common/panel/BusinessCard';
import ValidateTextArea from 'components/_common/forms/TextArea';
import { Pair } from 'types/Pair';
import { Dropdown } from 'primereact/dropdown';
import { DialogAction, DialogConfirm } from 'components/_new/Dialog';
import { Spinner } from 'components/_new/Spinner';
import { Application } from 'types/Application';
import { dateFormat } from 'utils/dateFormat';
import { dayjs } from 'utils/dayjs';
import { useGlobal } from 'contexts/globalContext';
import { Button } from 'components/_new/Button';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import PageMissing from 'components/PageMissing';
import { classNames } from 'primereact/utils';
import { prepareUserAreas } from 'utils/prepareUserAreas';
import { Message } from 'primereact/message';
import { scrollToElementById } from 'utils/scroll';
import { Box, BoxSection } from 'components/_new/Box';
import PairItem from 'components/userPanel/pairing/Pair';
import { useProgramPanel } from 'contexts/programPanelContext';
import PairActivityTimestamps from 'components/_common/PairActivityTimestamps';
import { getPairStatusInfo } from 'components/programPanel/utils';

const ExpandableRejectReason = ({ reason }: { reason: string }) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);

	if (!reason) return null;

	const isLong = reason.length > 150;
	const maxLength = 150;

	if (!isLong) {
		return (
			<>
				<br />
				{t('programPanel.application.rejectReason', { reason })}
			</>
		);
	}

	return (
		<>
			<br />
			{t('programPanel.application.rejectReason', {
				reason: isExpanded ? reason : `${reason.slice(0, maxLength)}...`,
			})}
			<span
				onClick={() => setIsExpanded(!isExpanded)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setIsExpanded(!isExpanded);
					}
				}}
				className="ml-1 text-primary cursor-pointer hover:underline"
				style={{
					fontSize: 'inherit',
					textDecoration: 'underline',
					display: 'inline',
					userSelect: 'none',
				}}
				aria-label={isExpanded ? 'Zwiń tekst' : 'Rozwiń pełny tekst'}
			>
				{isExpanded ? t('actions.showLess') : t('actions.expand')}
			</span>
		</>
	);
};

const useApplicationActions = (
	application: Application | undefined,
	program: any,
	handlers: any,
	loadingStates: any
) => {
	const { t } = useTranslation();

	return useMemo(() => {
		if (!application) return [];

		const actions = [];
		const { sending, restoringApplication, finalDeleteApplicationLoading, sendEditSuccess } = loadingStates;
		const {
			handleAccept,
			setRejectConfirmDialogOpen,
			setEditApplicationDialogOpen,
			handleCancelDeleting,
			setRestoreConfirmDialogOpen,
			setRemoveApplicationDialogOpen,
			setTransferDialogVisible,
		} = handlers;

		if (application.approved === 'not accepted') {
			actions.push(
				{
					id: 'accept-with-notification',
					label: t('actions.acceptWithNotification'),
					onClick: () => handleAccept(true),
					loading: sending,
					tooltip: t('programPanel.application.acceptWithNotificationTooltip'),
					variant: 'primary',
				},
				{
					id: 'accept-without-notification',
					label: t('actions.accept'),
					onClick: () => handleAccept(false),
					variant: 'primary-text',
					disabled: sending,
					tooltip: t('programPanel.application.acceptWithoutNotificationTooltip'),
				},
				{
					id: 'reject',
					label: t('actions.reject'),
					onClick: () => setRejectConfirmDialogOpen(true),
					variant: 'primary-text',
					disabled: sending,
				}
			);
		}

		if (application.approved === 'accepted') {
			actions.push({
				id: 'withdraw',
				label: t('programPanel.application.withdrawApplication'),
				onClick: () => setRejectConfirmDialogOpen(true),
				variant: 'primary-text',
				loading: sending,
			});
		}
		actions.push({
			id: 'transfer',
			label: t('programPanel.application.transferApplication'),
			onClick: () => setTransferDialogVisible(true),
			variant: 'primary-text',
			disabled: sending,
			tooltip: t('programPanel.application.transferApplicationTooltip'),
		});
		actions.push({
			id: 'edit',
			label: t('programPanel.application.editApplicationData'),
			onClick: () => setEditApplicationDialogOpen(true),
			variant: 'primary-text',
			loading: sending,
			disabled: !application,
		});

		if (application.deletedAt) {
			actions.push({
				id: 'cancel-delete',
				label: t('programPanel.pairing.removeApplication.cancelAction', {
					datetime: dateFormat(dayjs(application.deletedAt).toDate(), 'date'),
				}),
				onClick: handleCancelDeleting,
				variant: 'primary-text',
				loading: sending,
			});
		}

		if (application.approved === 'rejected') {
			actions.push({
				id: 'restore',
				label: t('programPanel.application.restoreApplication'),
				onClick: () => setRestoreConfirmDialogOpen(true),
				variant: 'primary-text',
				loading: restoringApplication,
			});
		}

		if (!application.deletedAt) {
			actions.push({
				id: 'remove',
				label: t('programPanel.pairing.removeApplication.removeAction'),
				onClick: () => setRemoveApplicationDialogOpen(true),
				variant: 'primary-text',
				loading: finalDeleteApplicationLoading,
			});
		}

		return actions;
	}, [application, loadingStates, handlers, t]);
};

const ActionButton = ({ action }: { action: any }) => (
	<>
		{action.tooltip && <Tooltip target={`#${action.id}-btn`} position="top" />}
		<Button
			id={`${action.id}-btn`}
			onClick={action.onClick}
			label={action.label}
			variant={action.variant}
			loading={action.loading}
			disabled={action.disabled}
			data-pr-tooltip={action.tooltip}
		/>
	</>
);

export const CoordinatorApplicationDetails = () => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();
	const { data: program, isSuccess } = useProgramPanel();
	const params = useParams<{ applicationId: string }>();
	const applicationId = Number(params.applicationId);

	// States
	const [manualModel, setManualMode] = useState(false);
	const [transferDialogVisible, setTransferDialogVisible] = useState(false);
	const [pairs, setPairs] = useState<Pair[]>([]);
	const [rejectConfirmDialogOpen, setRejectConfirmDialogOpen] = useState(false);
	const [editApplicationDialogOpen, setEditApplicationDialogOpen] = useState(false);
	const [currentPair, setCurrentPair] = useState<null | number>(null);
	const [acceptPairDialogOpen, setAcceptPairDialogOpen] = useState(false);
	const [removePairDialogOpen, setRemovePairDialogOpen] = useState(false);
	const [restoreConfirmDialogOpen, setRestoreConfirmDialogOpen] = useState(false);
	const [removeApplicationDialogOpen, setRemoveApplicationDialogOpen] = useState(false);
	const [applicationToAdd, setApplicationToAdd] = useState<null | number>(null);

	const {
		data: application,
		isLoading: applicationLoading,
		isError: applicationError,
		refetch: refetchApplication,
	} = useQuery(
		['COORDINATOR_GET_APPLICATION', applicationId],
		() => programsApi.getProgramApplicationCoordinator({ programId: Number(program?.id), applicationId }),
		{
			enabled: Boolean(isSuccess && Number(program?.id) && applicationId),
		}
	);

	const {
		isLoading: pairsLoading,
		refetch: refetchPairs,
		isRefetching,
	} = useQuery(
		['COORDINATOR_GET_APPLICATION_PAIRS', applicationId],
		() => programsApi.getProgramApplicationPairingCoordinator({ programId: Number(program?.id), applicationId }),
		{
			enabled: Boolean(isSuccess && Number(program?.id) && applicationId) && !manualModel,
			onSuccess: (data) => {
				if (data && data.length > 0) {
					setPairs(data);
				}
			},
		}
	);

	const { data: propositionsData, refetch: refetchPropositions } = useQuery(
		['COORDINATOR_GET_APPLICATION_PAIRS_PROPOSITIONS', program?.id],
		() =>
			programsApi.getAvailablePairsForApplicationCoordinator({
				programId: Number(program?.id),
				applicationId: Number(application?.id),
			}),
		{
			enabled: Boolean(isSuccess && Number(program?.id) && application?.id),
		}
	);

	// Mutations
	const {
		mutateAsync,
		isLoading: sending,
		isSuccess: sendEditSuccess,
	} = useMutation(programsApi.editProgramApplication, {
		onSuccess: () => {
			refetchApplication();
			setRejectConfirmDialogOpen(false);
		},
	});

	const { mutateAsync: acceptPair, isLoading: acceptPairLoading } = useMutation(programsApi.acceptProgramPair, {
		onSuccess: () => {
			setManualMode(true);
			setAcceptPairDialogOpen(false);
			setPairs((prev) =>
				prev.map((item) => {
					if (item.id === currentPair) {
						return {
							...item,
							acceptedByMentee: new Date().toISOString(),
							acceptedByMentor: new Date().toISOString(),
							entryMeeting: new Date().toISOString(),
							status: 'accepted',
						};
					}
					return item;
				})
			);
			setCurrentPair(null);
			refetchApplication();
			toastRef.current?.show({
				severity: 'success',
				life: 5000,
				summary: t('misc.success'),
				detail: t('programPanel.application.pairPropositions.acceptedSuccessInfo'),
			});
		},
	});

	const { mutateAsync: deletePair, isLoading: deletePairLoading } = useMutation(programsApi.deleteProgramPair, {
		onSuccess: () => {
			setManualMode(true);
			setRejectConfirmDialogOpen(false);
			setPairs((prev) => prev.filter((item) => item.id !== currentPair));
			setCurrentPair(null);
			refetchApplication();
			toastRef.current?.show({
				severity: 'success',
				life: 5000,
				summary: t('misc.success'),
				detail: t('programPanel.application.statusInfo.removed'),
			});
		},
	});

	const { mutate: addPair, isLoading: isAddingPair } = useMutation(programsApi.addProgramPair, {
		onSuccess: (newPair) => {
			refetchPropositions();
			if (newPair) {
				const preparedPair = {
					...newPair,
					programMembership:
						application?.applicationRole === 'mentor' ? newPair.menteeMembership : newPair.mentorMembership,
					application:
						application?.applicationRole === 'mentor'
							? newPair.menteeApplication
							: newPair.mentorApplication,
				};

				setPairs((prev) =>
					[...prev, preparedPair as unknown as Pair].sort((a, b) => {
						if (a.status === 'accepted' && b.status !== 'accepted') return -1;
						if (a.status === 'open' && b.status === 'accepted') return 1;
						if (a.createdAt > b.createdAt) return -1;
						if (a.createdAt < b.createdAt) return 1;
						return 0;
					})
				);

				setTimeout(() => {
					scrollToElementById(`pair-${newPair.id}`);
				}, 100);
			}
			setApplicationToAdd(null);
		},
	});

	const { mutate: deleteApplication, isLoading: deleteApplicationLoading } = useMutation(
		programsApi.deleteProgramApplications,
		{
			onSuccess: (data, variables) => {
				refetchApplication();
				toastRef.current?.show({
					severity: 'info',
					life: 5000,
					summary: t('misc.info'),
					detail: variables.quickDelete
						? t('programPanel.pairing.removeApplication.infoQuick')
						: t('programPanel.pairing.removeApplication.info'),
				});
			},
		}
	);

	const { mutate: cancelDeleteApplication, isLoading: cancelDeleteApplicationLoading } = useMutation(
		programsApi.cancelDeleteProgramApplication,
		{
			onSuccess: () => {
				refetchApplication();
			},
		}
	);

	const { mutateAsync: restoreApplication, isLoading: restoringApplication } = useMutation(
		programsApi.restoreProgramApplication,
		{
			onSuccess: () => {
				refetchApplication();
				toastRef.current?.show({
					severity: 'success',
					life: 5000,
					summary: t('misc.success'),
					detail: t('programPanel.application.statusInfo.restored'),
				});
			},
		}
	);

	// Handlers
	const handleAccept = useCallback(
		(withNotification = true) => {
			if (program && application) {
				mutateAsync({
					programId: program?.id,
					applicationId: application?.id,
					status: 'accepted',
					withNotification,
				});
			}
		},
		[program, application, mutateAsync]
	);

	const handleReject = useCallback(
		(reason: string) => {
			if (program && application) {
				return mutateAsync({
					programId: program?.id,
					applicationId: application?.id,
					status: 'rejected',
					reason,
				});
			}
			return undefined;
		},
		[program, application, mutateAsync]
	);

	const handleAcceptPair = useCallback(() => {
		if (currentPair && program) {
			return acceptPair({ programId: program.id, pairId: currentPair });
		}
		return undefined;
	}, [currentPair, program, acceptPair]);

	const handleRemovePair = useCallback(() => {
		if (currentPair && program) {
			return deletePair({ programId: program.id, pairId: currentPair });
		}
		return undefined;
	}, [currentPair, program, deletePair]);

	const handleCancelDeleting = useCallback(() => {
		cancelDeleteApplication({ programId: Number(program?.id), applicationId: Number(application?.id) });
	}, [program, application, cancelDeleteApplication]);

	const handleRestoreApplication = useCallback(() => {
		if (program && application) {
			restoreApplication({
				programId: program?.id,
				applicationId: application?.id,
			});
		}
	}, [program, application, restoreApplication]);

	// Forms
	const { control, trigger, getValues } = useForm<{ reason: string }>();

	// Computed values
	const finalDeleteApplicationLoading = cancelDeleteApplicationLoading || deleteApplicationLoading;

	const getApprovedText = (status: string) => {
		switch (status) {
			case 'accepted':
				return t('programPanel.application.statusOptions.accepted');
			case 'rejected':
				return t('programPanel.application.statusOptions.rejected');
			case 'not accepted':
			default:
				return t('programPanel.application.statusOptions.notAccepted');
		}
	};

	// Actions hook - hook zawsze wywołany
	const actions = useApplicationActions(
		application,
		program,
		{
			handleAccept,
			setRejectConfirmDialogOpen,
			setEditApplicationDialogOpen,
			handleCancelDeleting,
			setRestoreConfirmDialogOpen,
			setRemoveApplicationDialogOpen,
			setTransferDialogVisible,
		},
		{
			sending,
			restoringApplication,
			finalDeleteApplicationLoading,
			sendEditSuccess,
		}
	);

	const rejectApplicationDialogActions = [
		{
			key: 'reject',
			submit: true,
			label: t('actions.reject'),
			onClick: () => {
				trigger('reason').then((valid) => {
					if (valid) {
						handleReject(getValues('reason'));
					}
				});
			},
			loading: sending,
		},
	];

	if (applicationLoading) {
		return (
			<div className="flex justify-content-center">
				<Spinner />
			</div>
		);
	}

	if (applicationError) {
		return <PageMissing />;
	}

	if (!application) {
		return null;
	}

	return (
		<>
			<Helmet title={t('organizationPanel.programPairingTests')} />

			<SubPageTitle
				title={
					application?.applicationRole === 'mentor'
						? t('programPanel.application.titleForMentor')
						: t('programPanel.application.titleForMentee')
				}
				additional={program?.displayName}
			/>

			{/* Dialogi */}
			<DialogConfirm
				message={t('programPanel.application.confirmRemovePairProposition')}
				visible={removePairDialogOpen}
				onHide={() => setRemovePairDialogOpen(false)}
				onConfirm={handleRemovePair}
				confirmAction={{ loading: deletePairLoading }}
			/>

			<DialogConfirm
				message={t('programPanel.application.confirmCreatePair')}
				visible={acceptPairDialogOpen}
				onHide={() => setAcceptPairDialogOpen(false)}
				onConfirm={handleAcceptPair}
				confirmAction={{ loading: acceptPairLoading }}
			/>

			<DialogConfirm
				message={t('programPanel.application.confirmRestoreApplication')}
				visible={restoreConfirmDialogOpen}
				onHide={() => setRestoreConfirmDialogOpen(false)}
				onConfirm={handleRestoreApplication}
				confirmAction={{ loading: restoringApplication }}
			/>

			{/* <DeleteApplicationConfirmDialog
				control={deleteApplicationControl}
				onConfirm={handleFormSubmit}
				visible={removeApplicationDialogOpen}
				onHide={() => setRemoveApplicationDialogOpen(false)}
				confirmAction={{ loading: deleteApplicationLoading }}
			/>

			<EditApplicationDialog
				visible={editApplicationDialogOpen}
				onHide={() => setEditApplicationDialogOpen(false)}
				applicationData={application}
				programMembershipId={application?.programMembership.id}
				applicationRole={application.applicationRole}
				editMode
				programId={application.programId}
				onApplicationAdded={refetchApplication}
				onCancel={() => {}}
			/>

			<TransferApplicationDialog
				visible={transferDialogVisible}
				onHide={() => setTransferDialogVisible(false)}
				application={application}
				programId={Number(program?.id)}
				onTransferSuccess={() => {
					refetchApplication();
				}}
			/> */}

			<DialogAction
				title={t('programPanel.application.enterRejectReason')}
				visible={rejectConfirmDialogOpen}
				onHide={() => setRejectConfirmDialogOpen(false)}
				actions={rejectApplicationDialogActions}
			>
				<ValidateTextArea name="reason" control={control} required />
			</DialogAction>

			{/* Główna zawartość */}
			<div className="flex flex-row justify-content-between align-items-center">
				<h2>{t('programPanel.application.pairTest')}</h2>
				{application?.limitStats && (
					<div>
						{`${t('programPanel.application.pairPropositions.acceptedPairs')}: `}
						<span
							className={classNames({
								'text-success font-bold': application.limitStats.pairLimitReached,
							})}
						>
							{application.limitStats.currentPairs}/{application.pairLimit}
						</span>
					</div>
				)}
			</div>

			<Box variant="white-bordered">
				<BoxSection>
					<BusinessCard
						configPanel
						data={application}
						highlightDeleted
						displayHidden
						renderBottom={() => {
							const hasLongRejectReason =
								application.rejectReason && application.rejectReason.length > 100;

							return (
								<div
									className={`flex gap-2 justify-content-between ${
										hasLongRejectReason ? 'flex-column' : 'flex-column lg:flex-row'
									}`}
								>
									<span className="min-w-4rem">
										{t('programPanel.application.applicationHasStatus', {
											status: application.finished
												? t('programPanel.application.statusOptions.finished')
												: getApprovedText(application.approved),
										})}
										<ExpandableRejectReason reason={application.rejectReason || ''} />
									</span>
									<div
										className={`flex flex-row flex-wrap gap-2 ${
											hasLongRejectReason ? 'justify-content-start mt-3' : 'justify-content-end'
										}`}
									>
										{actions.map((action) => (
											<ActionButton key={action.id} action={action} />
										))}
									</div>
								</div>
							);
						}}
					/>
				</BoxSection>
			</Box>
		</>
	);
};
